import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { getPendingOrganizationSwitchEvent } from '../utils/organizationSwitchSession.utils'

import type { OrganizationSwitchTabSyncEvent } from '@v2/commons/hooks/useTabSync'
import {
  recordExplicitLogin,
  resetAuthStore,
} from '@v2/domains/auth/store/auth.store'

export type OrganizationSwitchSource = //TODO refine this type based on actual sources of organization switch in the app
  'manual' | 'guard' | 'cross-tab' | 'login' | 'unknown'

export interface OrganizationSwitchState {
  status: 'idle' | 'switching'
  targetOrganizationId: string | null
  targetPath: string | null
  source: OrganizationSwitchSource | null
  pendingEvent: OrganizationSwitchTabSyncEvent | null
}

interface StartOrganizationSwitchPayload {
  targetOrganizationId: string
  targetPath?: string
  source?: OrganizationSwitchSource
}

const initialState: OrganizationSwitchState = {
  status: 'idle',
  targetOrganizationId: null,
  targetPath: null,
  source: null,
  pendingEvent: null,
}

const organizationSwitchStore = createSlice({
  name: 'organizationSwitch',
  initialState: (): OrganizationSwitchState => ({
    ...initialState,
    pendingEvent: getPendingOrganizationSwitchEvent(),
  }),
  reducers: {
    startOrganizationSwitch: (
      state,
      action: PayloadAction<StartOrganizationSwitchPayload>,
    ) => {
      state.status = 'switching'
      state.targetOrganizationId = action.payload.targetOrganizationId
      state.targetPath = action.payload.targetPath ?? null
      state.source = action.payload.source ?? 'unknown'
    },
    finishOrganizationSwitch: (state) => ({
      ...initialState,
      pendingEvent: state.pendingEvent,
    }),
    receiveOrganizationSwitchEvent: (
      state,
      action: PayloadAction<OrganizationSwitchTabSyncEvent>,
    ) => {
      if (
        state.pendingEvent?.eventId !== action.payload.eventId &&
        (!state.pendingEvent ||
          action.payload.timestamp >= state.pendingEvent.timestamp)
      ) {
        state.pendingEvent = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(recordExplicitLogin, (state) => {
      state.pendingEvent = null
    })
    builder.addCase(resetAuthStore, () => initialState)
  },
})

export const {
  startOrganizationSwitch,
  finishOrganizationSwitch,
  receiveOrganizationSwitchEvent,
} = organizationSwitchStore.actions

export default organizationSwitchStore.reducer
